/**
 * Search Query Enricher
 *
 * Builds compound Amazon-optimized search queries by combining:
 *   - product keywords (from AI or user input)
 *   - profile context (skin type, concerns, age group)
 *   - conversation context (baby, specific ingredients, etc.)
 *
 * Also provides helpers for:
 *   - Extracting contextual keywords from user messages
 *   - Amazon ASIN dedup (parent ASIN extraction from /dp/ URLs)
 *   - Filtering non-product Amazon pages (/s?, /live/, /stores/)
 *   - Preferring /dp/ product detail pages
 */

import type { MasterProfile } from '../types/profile';

// ---------------------------------------------------------------------------
// Conversation context keyword patterns
// ---------------------------------------------------------------------------

/** Keywords that capture special audience / use-case modifiers from chat. */
const CONTEXT_KEYWORDS_EN: Array<{ pattern: RegExp; keyword: string }> = [
  { pattern: /\bbab(y|ies)\b/i, keyword: 'baby' },
  { pattern: /\btoddler/i, keyword: 'baby' },
  { pattern: /\binfant/i, keyword: 'baby' },
  { pattern: /\bkids?\b/i, keyword: 'kids' },
  { pattern: /\bchildren\b/i, keyword: 'kids' },
  { pattern: /\bteen(ager)?s?\b/i, keyword: 'teen' },
  { pattern: /\bpregnant|pregnancy|prenatal/i, keyword: 'pregnancy-safe' },
  { pattern: /\bmaternity|expecting\b/i, keyword: 'pregnancy-safe' },
  { pattern: /\bsensitive\s*skin\b/i, keyword: 'sensitive skin' },
  { pattern: /\beczema\b/i, keyword: 'eczema' },
  { pattern: /\brosacea\b/i, keyword: 'rosacea' },
  { pattern: /\bfragrance[\s-]*free\b/i, keyword: 'fragrance free' },
  { pattern: /\bvegan\b/i, keyword: 'vegan' },
  { pattern: /\borganic\b/i, keyword: 'organic' },
  { pattern: /\bcruelty[\s-]*free\b/i, keyword: 'cruelty free' },
  { pattern: /\bdermatologist/i, keyword: 'dermatologist recommended' },
  { pattern: /\bSPF\b/i, keyword: 'SPF' },
  { pattern: /\banti[\s-]*aging\b/i, keyword: 'anti-aging' },
];

const CONTEXT_KEYWORDS_KO: Array<{ pattern: RegExp; keyword: string }> = [
  { pattern: /아기|베이비|영유아/i, keyword: '아기' },
  { pattern: /어린이|아이|키즈/i, keyword: '어린이' },
  { pattern: /청소년|10대/i, keyword: '청소년' },
  { pattern: /임산부|임신|태교/i, keyword: '임산부용' },
  { pattern: /민감[성\s]*피부/i, keyword: '민감성' },
  { pattern: /아토피/i, keyword: '아토피' },
  { pattern: /주사비|주사/i, keyword: '주사비' },
  { pattern: /무향/i, keyword: '무향' },
  { pattern: /비건/i, keyword: '비건' },
  { pattern: /유기농/i, keyword: '유기농' },
  { pattern: /동물실험\s*(?:안|미)/i, keyword: '동물실험 미실시' },
  { pattern: /피부과/i, keyword: '피부과 추천' },
  { pattern: /자외선\s*차단|선크림/i, keyword: 'SPF' },
  { pattern: /안티에이징|항노화/i, keyword: '안티에이징' },
];

/**
 * Extract contextual modifiers from the last N user messages.
 * Returns de-duplicated keywords like "baby", "sensitive skin", etc.
 */
export function extractConversationContext(
  messages: Array<{ sender: string; text?: string }>,
  language: string,
): string[] {
  const patterns = language === 'ko' ? CONTEXT_KEYWORDS_KO : CONTEXT_KEYWORDS_EN;
  const found = new Set<string>();

  // Scan the last 6 user messages for context clues
  const recentUserMsgs = messages
    .filter(m => m.sender === 'user' && m.text)
    .slice(-6);

  for (const msg of recentUserMsgs) {
    const text = msg.text!;
    for (const { pattern, keyword } of patterns) {
      if (pattern.test(text)) {
        found.add(keyword);
      }
    }
  }

  return Array.from(found);
}

// ---------------------------------------------------------------------------
// Compound query builder
// ---------------------------------------------------------------------------

/**
 * Enrich a bare keyword (e.g. "serum") into a compound query
 * (e.g. "niacinamide serum for dry skin").
 *
 * Strategy:
 *   1. If the keyword already looks compound (>= 3 words), leave it alone.
 *   2. Otherwise append profile qualifiers (skin type, top concern, age hint)
 *      and any conversation context modifiers.
 */
export function buildCompoundQuery(
  keyword: string,
  profile: MasterProfile | null | undefined,
  contextKeywords: string[],
  language: string,
): string {
  // Already compound enough — don't over-stuff
  if (keyword.split(/\s+/).length >= 3) return keyword;

  const parts: string[] = [keyword];

  // Add top skin concern as qualifier
  const topConcern = profile?.skin?.concerns?.[0];
  if (topConcern && !keyword.toLowerCase().includes(topConcern.toLowerCase())) {
    parts.push(topConcern);
  }

  // Skin type qualifier (localized for Korean)
  const skinType = profile?.skin?.type;
  if (skinType && !keyword.toLowerCase().includes(skinType.toLowerCase())) {
    const SKIN_TYPE_KO: Record<string, string> = {
      dry: '건성', oily: '지성', combination: '복합성', sensitive: '민감성', normal: '중성',
    };
    if (language === 'ko') {
      const koType = SKIN_TYPE_KO[skinType.toLowerCase()] || skinType;
      parts.push(`${koType} 피부`);
    } else {
      parts.push(`${skinType} skin`);
    }
  }

  // Conversation context modifiers (baby, fragrance free, etc.)
  for (const ctx of contextKeywords.slice(0, 2)) {
    if (!keyword.toLowerCase().includes(ctx.toLowerCase())) {
      parts.push(ctx);
    }
  }

  // Cap total length to avoid over-long queries (Google truncates at ~32 words)
  return parts.slice(0, 5).join(' ');
}

// ---------------------------------------------------------------------------
// Amazon URL helpers
// ---------------------------------------------------------------------------

/** Regex to extract ASIN from Amazon /dp/ URLs */
const ASIN_RE = /\/dp\/([A-Z0-9]{10})\b/i;

/**
 * Extract Amazon ASIN from a URL. Returns null for non-product pages.
 */
export function extractAsin(url: string): string | null {
  const match = ASIN_RE.exec(url);
  return match ? match[1].toUpperCase() : null;
}

/**
 * True if the URL points to an Amazon product detail page (/dp/, /gp/product/).
 * Returns false for search result pages (/s?), Live, creator stores, etc.
 * Non-Amazon URLs return false (use a separate check if needed).
 */
export function isAmazonProductPage(url: string): boolean {
  try {
    const lower = url.toLowerCase();
    // Must be Amazon
    if (!lower.includes('amazon.')) return false;
    // Reject search listings, live streams, creator storefronts
    if (lower.includes('/s?') || lower.includes('/s/')) return false;
    if (lower.includes('/live/')) return false;
    if (lower.includes('/stores/')) return false;
    if (lower.includes('/creator/')) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Deduplicate search results by Amazon ASIN (extracted from /dp/ URLs).
 * Non-Amazon results always pass through.
 * Among Amazon results sharing the same ASIN, keep the first one seen.
 */
export function deduplicateByAsin<T extends { link: string }>(results: T[]): T[] {
  const seenAsins = new Set<string>();
  return results.filter(r => {
    const asin = extractAsin(r.link);
    if (!asin) return true; // non-Amazon or non-/dp/ — keep
    if (seenAsins.has(asin)) return false;
    seenAsins.add(asin);
    return true;
  });
}
